/*
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { getCurrencySymbol } from "./currencies";
import { logger } from "@/lib/logger";
import {
  getProductDownloadUrl,
  DEFAULT_DOWNLOAD_URL,
  DEPRECATED_LIFETIME_PRODUCT_IDS,
} from "@/lib/constants";
import { DOCUMENT_TRANSLATOR_PRODUCT_ID } from "@/lib/document-translator-pricing";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Generate a UUID v4 with cross-browser compatibility.
 *
 * Uses crypto.randomUUID() if available (modern browsers),
 * falls back to crypto.getRandomValues() for broader support,
 * and finally to Math.random() as last resort.
 *
 * @returns A UUID v4 string (e.g., "550e8400-e29b-41d4-a716-446655440000")
 */
export function generateUUID(): string {
  // Method 1: Use native crypto.randomUUID if available (modern browsers)
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    try {
      return crypto.randomUUID();
    } catch {
      // Fall through to next method
    }
  }

  // Method 2: Use crypto.getRandomValues (broader browser support)
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.getRandomValues === "function"
  ) {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);

    // Set version (4) and variant (RFC4122) bits
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // Version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // Variant RFC4122

    const hex = Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");

    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  // Method 3: Fallback using Math.random (last resort)
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Billing dates from Stripe (renewal, period start/end, invoice date, cancel
// date) are Unix timestamps that mark a precise UTC moment chosen by Stripe's
// billing-cycle math. They must be rendered as the UTC calendar date so the
// displayed value is stable and authoritative for every viewer. Without
// timeZone: "UTC", a viewer in a UTC-negative zone (e.g. the Americas) sees a
// period end of 2026-07-01 00:30 UTC as "30 Jun 2026" — a full day earlier
// than the real renewal date, which looks like a billing error to the customer.
// S487/Fix 4: Guard against null/undefined/0 timestamps. Stripe can return
// null for current_period_end in certain edge cases (e.g. incomplete subs).
// Without the guard, null * 1000 = 0 → "1 Jan 1970" shown as the renewal date.
export function formatDate(timestamp: number | null | undefined): string {
  if (!timestamp) return "—";
  const date = new Date(timestamp * 1000);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

export function formatDateFromDate(date: Date) {
  return date.toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

export const formatCurrency = (currency: string, amount: number) =>
  `${getCurrencySymbol(currency.toUpperCase())}${(amount / 100).toFixed(2)}`;

export const formatCurrencyCart = (currency: string, amount: number) =>
  `${getCurrencySymbol(currency.toUpperCase())} ${(amount / 100).toFixed(2)}`;

export const getLicenseBadgeStyle = (type: string) => {
  switch (type) {
    case "Monthly":
      return "bg-orange-100 text-orange-700 border-orange-200";
    case "Annual":
      return "bg-blue-100 text-blue-600 border-blue-200";
    case "Lifetime":
      return "bg-green-200 text-green-800 border-green-200";
    default:
      return "bg-gray-100 text-gray-600 border-gray-200";
  }
};

export const handleDownloadClick = (productId?: string) => {
  const url = productId
    ? getProductDownloadUrl(productId)
    : DEFAULT_DOWNLOAD_URL;
  window.open(url, "_blank", "noopener,noreferrer");
};

export const formatCartProductName = (name: string) => {
  if (!/ - | – /.test(name)) {
    return { main: name, rest: "" };
  }
  const [main, ...rest] = name.split(/ - | – /) || [];
  return {
    main,
    rest: rest.length > 0 ? rest.join(" - ") : "",
  };
};

/**
 * Append the license-type suffix to a base Stripe product name for display.
 *
 * S256 (FCR_ShoppingCart_Session255_v4 §3): centralises the license-type
 * suffix so every display surface shows the same string — "Auto FTP Manager
 * v8 - Annual License", "Dictation Pro v2 - Lifetime License", and so on.
 * Previously the suffix was appended inline in `cart-product-row.tsx` for
 * the add-to-cart page only; every other surface (invoice table, product
 * card, product table, license table, license summary, subscription row,
 * device table, dashboard widgets) inherited the bare Stripe product name
 * and showed it without the license-type tag.
 *
 * Idempotent: if the suffix is already present in `productName` the
 * function returns `productName` unchanged.
 *
 * Pay-per-use and consumable products (Document Translator, TSL Extra
 * Words) have no license tier — the function returns the base name with
 * any trailing " - " / "-" / whitespace stripped, so names like
 * "Document Translator v2 - Price Per Word - " render cleanly.
 *
 * @param productName - The base Stripe product name (may be empty/nullish)
 * @param productType - Stripe `price.type`: "one_time" | "recurring"
 * @param interval    - Stripe `price.recurring.interval`: "year" | "month"
 * @param productId   - Stripe product ID for pay-per-use detection
 * @returns The display name with the license-type suffix appended
 *
 * @example
 * getProductDisplayName("Auto FTP Manager v8", "one_time")
 *   // → "Auto FTP Manager v8 - Lifetime License"
 * getProductDisplayName("Dictation Pro v2", "recurring", "year")
 *   // → "Dictation Pro v2 - Annual License"
 * getProductDisplayName("Text Speaker Live v4 - 45000 Words", "recurring", "month")
 *   // → "Text Speaker Live v4 - 45000 Words - Monthly"
 * getProductDisplayName("Document Translator v2 - Price Per Word - ",
 *                        "one_time", undefined, DOCUMENT_TRANSLATOR_PRODUCT_ID)
 *   // → "Document Translator v2 - Price Per Word"
 */
export function getProductDisplayName(
  productName: string,
  productType: "recurring" | "one_time" | undefined,
  interval?: string,
  productId?: string,
  priceLabel?: string,
): string {
  // SS-6 (FCR Pass 14): Prefer the price-level label (nickname or a label
  // derived from price metadata like word_count) over the Stripe product name.
  // Stripe's data model places tier identity (word count, interval) on the
  // price, not the product -- so "Text Speaker Live v4" from the product name
  // has no word count, while the price object does. When callers supply a
  // priceLabel it becomes the base so the suffix logic still applies correctly.
  const base = (priceLabel && priceLabel.trim()) ? priceLabel.trim() : (productName ?? "");
  if (
    (productId !== undefined && DEPRECATED_LIFETIME_PRODUCT_IDS.has(productId)) ||
    productId === DOCUMENT_TRANSLATOR_PRODUCT_ID
  ) {
    // Strip any trailing " - " / "-" / whitespace left over from Stripe's
    // product naming (e.g., "Document Translator v2 - Price Per Word - ").
    // Also catches deprecated one-time products in historical invoices so
    // they don't pick up an incorrect "Lifetime License" suffix.
    return base.replace(/[\s-]+$/, "");
  }

  let suffix: string;
  if (productType === "one_time") {
    suffix = "Lifetime License";
  } else if (productType === "recurring") {
    if (interval === "year") {
      suffix = "Annual License";
    } else if (interval === "month") {
      suffix = "Monthly";
    } else {
      return base;
    }
  } else {
    return base;
  }

  return base.includes(suffix) ? base : `${base} - ${suffix}`;
}

/**
 * Parse discount breakdown JSON safely
 *
 * Parses the discount_breakdown JSON string from session/invoice metadata.
 * Returns null if parsing fails or input is invalid.
 *
 * @param discountBreakdownJson - JSON string from metadata.discount_breakdown
 * @returns Parsed DiscountBreakdown object or null
 *
 * @example
 * // Input: '{"prod_XXX":{"amount":1000,"pct":10}}'
 * // Output: { "prod_XXX": { amount: 1000, pct: 10 } }
 */
export function parseDiscountBreakdown(
  discountBreakdownJson: string | undefined | null,
): Record<string, { amount: number; pct: number }> | null {
  if (!discountBreakdownJson || typeof discountBreakdownJson !== "string") {
    return null;
  }

  try {
    const parsed = JSON.parse(discountBreakdownJson);

    // Validate structure: must be an object with product ID keys
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      logger.warn("Invalid discount breakdown structure: not an object");
      return null;
    }

    // Validate each entry has amount and pct
    for (const [productId, entry] of Object.entries(parsed)) {
      const discountEntry = entry as Record<string, unknown>;
      if (
        typeof entry !== "object" ||
        entry === null ||
        typeof discountEntry.amount !== "number" ||
        typeof discountEntry.pct !== "number"
      ) {
        logger.warn(`Invalid discount entry for product ${productId}:`, entry);
        return null;
      }
    }

    return parsed as Record<string, { amount: number; pct: number }>;
  } catch (error) {
    logger.warn("Failed to parse discount breakdown JSON:", error);
    return null;
  }
}

/**
 * Get discount details for a specific product from the breakdown
 *
 * @param breakdown - Parsed discount breakdown object
 * @param productId - Stripe product ID to look up
 * @returns Discount entry or null if not found
 */
export function getProductDiscount(
  breakdown: Record<string, { amount: number; pct: number }> | null,
  productId: string | undefined,
): { amount: number; pct: number } | null {
  if (!breakdown || !productId) {
    return null;
  }
  return breakdown[productId] || null;
}
